"use client";

import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, Star, Gift, Lock, RefreshCcw } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useProfileContext } from "../../context/ProfileContext";
import {
  getToyShelfProgress,
  TOY_PROGRESS_UPDATED_EVENT,
  TOY_SLOT_EMOJIS,
  TOY_SLOT_NAMES,
  TOY_SLOT_RARITIES,
  TOY_TARGET_PER_SLOT,
} from "../../services/toyCollection";

type CollectibleTab = "collection" | "progress";

type CollectibleItem = {
  id: number;
  name: string;
  description: string;
  emoji: string;
  category: string;
  rarity: "Common" | "Rare" | "Epic";
  rarityColor: string;
  bgColor: string;
  count: number;
  target: number;
  progressPercent: number;
  isCollected: boolean;
  isCompleted: boolean;
};

const rarityColorMap: Record<CollectibleItem["rarity"], string> = {
  Common: "bg-gray-500",
  Rare: "bg-blue-500",
  Epic: "bg-purple-500",
};

const rarityBgColorMap: Record<CollectibleItem["rarity"], string> = {
  Common: "from-blue-100 to-slate-200",
  Rare: "from-amber-100 to-yellow-200",
  Epic: "from-purple-100 to-pink-200",
};

const EMPTY_TOY_PROGRESS = {
  slots: Array.from({ length: TOY_SLOT_EMOJIS.length }, () => 0),
  lastCollectedSlot: null as number | null,
};

const MyCollectibles = () => {
  const router = useNavigate();
  const { selectedProfile } = useProfileContext();
  const [selectedTab, setSelectedTab] = useState<CollectibleTab>("collection");
  const [clickedItem, setClickedItem] = useState<number | null>(null);
  const [toyProgress, setToyProgress] = useState(EMPTY_TOY_PROGRESS);

  const refreshProgress = useCallback(() => {
    void (async () => {
      const progress = await getToyShelfProgress(selectedProfile?.studentId);
      setToyProgress(progress);
    })();
  }, [selectedProfile?.studentId]);

  useEffect(() => {
    refreshProgress();
  }, [refreshProgress]);

  useEffect(() => {
    const onToyProgressUpdated = () => {
      refreshProgress();
    };
    window.addEventListener(TOY_PROGRESS_UPDATED_EVENT, onToyProgressUpdated);
    return () =>
      window.removeEventListener(TOY_PROGRESS_UPDATED_EVENT, onToyProgressUpdated);
  }, [refreshProgress]);

  const handleItemClick = (id: number) => {
    setClickedItem(id);
    setTimeout(() => setClickedItem(null), 400);
  };

  const collectibleItems = useMemo<CollectibleItem[]>(() => {
    return TOY_SLOT_EMOJIS.map((emoji, index) => {
      const count = toyProgress.slots[index] ?? 0;
      const rarity = TOY_SLOT_RARITIES[index] ?? "Common";
      const target = TOY_TARGET_PER_SLOT;
      const progressPercent = Math.round((count / target) * 100);
      const isCollected = count > 0;
      const isCompleted = count >= target;

      return {
        id: index + 1,
        name: TOY_SLOT_NAMES[index] ?? `Toy ${index + 1}`,
        description: isCompleted
          ? "Fully collected. Amazing progress!"
          : isCollected
          ? "Collected and growing stronger with every correct answer."
          : "Not collected yet. Practice more words to unlock this toy.",
        emoji,
        category: "Toy",
        rarity,
        rarityColor: rarityColorMap[rarity],
        bgColor: rarityBgColorMap[rarity],
        count,
        target,
        progressPercent,
        isCollected,
        isCompleted,
      };
    });
  }, [toyProgress.slots]);

  const collectedItems = useMemo(
    () => collectibleItems.filter((item) => item.isCollected),
    [collectibleItems]
  );

  const progressItems = useMemo(
    () => collectibleItems.filter((item) => !item.isCompleted),
    [collectibleItems]
  );

  const totalPiecesCollected = useMemo(
    () => toyProgress.slots.reduce((sum, count) => sum + count, 0),
    [toyProgress.slots]
  );

  const completedSetsCount = useMemo(
    () => toyProgress.slots.filter((count) => count >= TOY_TARGET_PER_SLOT).length,
    [toyProgress.slots]
  );

  const activeItems = selectedTab === "collection" ? collectedItems : progressItems;

  const renderCollectibleCard = (item: CollectibleItem, showProgress = false) => (
    <div
      key={item.id}
      onClick={() => handleItemClick(item.id)}
      className={`group relative bg-gradient-to-br ${
        item.bgColor
      } rounded-3xl p-6 shadow-xl hover:shadow-2xl transform hover:scale-105 transition-all duration-300 cursor-pointer border-4 border-white/40 ${
        clickedItem === item.id ? "animate-pulse scale-95" : ""
      } ${!item.isCollected ? "opacity-75" : ""}`}
    >
      {!item.isCollected && (
        <div className="absolute top-4 right-4 bg-gray-500 text-white rounded-full p-2">
          <Lock className="w-4 h-4" />
        </div>
      )}

      <div className="text-center">
        <div
          className={`text-6xl mb-4 group-hover:scale-110 transition-transform duration-300 ${
            !item.isCollected ? "grayscale" : ""
          }`}
        >
          {item.isCollected ? item.emoji : "?"}
        </div>

        <h3 className="text-xl font-bold text-gray-800 mb-2 group-hover:text-purple-600 transition-colors">
          {item.name}
        </h3>

        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          {item.description}
        </p>

        <div className="flex items-center justify-between mb-3">
          <span className="text-xs font-semibold text-gray-500 bg-white/60 px-3 py-1 rounded-full">
            {item.category}
          </span>
          <span
            className={`text-xs font-bold text-white px-3 py-1 rounded-full ${item.rarityColor}`}
          >
            {item.rarity}
          </span>
        </div>

        {showProgress ? (
          <div className="space-y-2">
            <div className="text-xs text-gray-600 font-medium">
              Progress: {item.count}/{item.target}
            </div>
            <div className="w-full bg-gray-300 rounded-full h-2">
              <div
                className="bg-gradient-to-r from-purple-400 to-pink-400 h-2 rounded-full transition-all duration-500"
                style={{ width: `${item.progressPercent}%` }}
              ></div>
            </div>
            <div className="text-xs text-gray-500">{item.progressPercent}% complete</div>
          </div>
        ) : (
          <div
            className={`text-white text-sm font-bold py-2 px-4 rounded-full transition-colors ${
              item.isCompleted
                ? "bg-blue-500 group-hover:bg-blue-600"
                : "bg-green-500 group-hover:bg-green-600"
            }`}
          >
            {item.isCompleted ? "Mastered! ★" : "Collected! ✓"}
          </div>
        )}
      </div>

      {/* Decorative elements */}
      <div className="absolute -right-4 -top-4 w-16 h-16 bg-white/20 rounded-full group-hover:scale-125 transition-transform duration-500"></div>
      <div className="absolute -left-2 -bottom-2 w-10 h-10 bg-white/15 rounded-full group-hover:scale-110 transition-transform duration-700"></div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-200 via-purple-200 to-indigo-200 p-6">
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <div className="bg-white/90 backdrop-blur-sm rounded-3xl p-6 shadow-xl border border-white/30">
            <div className="flex items-center gap-4 mb-4">
              <button
                onClick={() => router("/profile")}
                className="p-3 bg-gradient-to-r from-purple-400 to-pink-400 text-white rounded-full hover:scale-110 transition-transform duration-200"
              >
                <ArrowLeft className="w-6 h-6" />
              </button>
              <div>
                <h1 className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                  My Collectibles
                </h1>
                <p className="text-gray-600">
                  Hello, {selectedProfile?.studentName ?? "Learner"}! You have {totalPiecesCollected} toy pieces
                </p>
                <p className="text-sm text-gray-500">
                  {completedSetsCount}/{TOY_SLOT_EMOJIS.length} full sets completed
                </p>
              </div>
              <div className="ml-auto text-4xl animate-bounce">🎁</div>
            </div>

            {/* Tab Navigation */}
            <div className="flex gap-4">
              <button
                onClick={() => setSelectedTab("collection")}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                  selectedTab === "collection"
                    ? "bg-gradient-to-r from-purple-400 to-pink-400 text-white shadow-lg transform scale-105"
                    : "bg-white/60 text-gray-600 hover:bg-white/80"
                }`}
              >
                <Gift className="w-5 h-5" />
                My Collection
              </button>
              <button
                onClick={() => setSelectedTab("progress")}
                className={`flex items-center gap-2 px-6 py-3 rounded-2xl font-semibold transition-all duration-300 ${
                  selectedTab === "progress"
                    ? "bg-gradient-to-r from-blue-400 to-cyan-400 text-white shadow-lg transform scale-105"
                    : "bg-white/60 text-gray-600 hover:bg-white/80"
                }`}
              >
                <Lock className="w-5 h-5" />
                In Progress
              </button>
              <button
                onClick={refreshProgress}
                className="ml-auto flex items-center gap-2 px-4 py-3 rounded-2xl font-semibold bg-white/70 text-gray-700 hover:bg-white/90 transition-all"
              >
                <RefreshCcw className="w-4 h-4" />
                Refresh
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="space-y-8">
          <div>
            <h2 className="text-2xl font-bold text-gray-800 mb-6 flex items-center gap-3">
              <Star className="w-8 h-8 text-yellow-500" />
              {selectedTab === "collection"
                ? "Your Amazing Collection"
                : "Unlock More Toys"}
            </h2>

            {activeItems.length === 0 ? (
              <div className="rounded-3xl border border-white/40 bg-white/70 p-8 text-center shadow-lg">
                <p className="text-xl font-semibold text-gray-700">
                  No items here yet.
                </p>
                <p className="mt-2 text-gray-600">
                  Practice a few words to start collecting toys.
                </p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {activeItems.map((item) =>
                  renderCollectibleCard(item, selectedTab === "progress")
                )}
              </div>
            )}
          </div>
        </div>

        {/* Floating Decorative Elements */}
        <div
          className="fixed top-16 right-8 text-4xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "0.5s" }}
        >
          🎈
        </div>
        <div
          className="fixed bottom-32 left-8 text-3xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "1.2s" }}
        >
          🌟
        </div>
        <div
          className="fixed top-1/3 left-4 text-2xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "2s" }}
        >
          ✨
        </div>
        <div
          className="fixed top-2/3 right-6 text-3xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "0.8s" }}
        >
          🎉
        </div>
        <div
          className="fixed bottom-1/4 right-20 text-2xl animate-bounce hover:scale-125 transition-transform cursor-pointer opacity-70"
          style={{ animationDelay: "1.5s" }}
        >
          🦄
        </div>
      </div>
    </div>
  );
};

export default MyCollectibles;
